/**
 * Admin users API client.
 */

import { Result } from "true-myth/result";

import type { AuthStrategy } from "../../config/api-config";
import type { Role } from "../../rbac/permissions";
import type {
  AdminApiError,
  ListUsersOptions,
  ListUsersResponse,
} from "./types";

function createAuthFetch(auth: AuthStrategy) {
  return (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (auth.type === "bearer") {
      const token = auth.getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string>),
      },
      credentials: auth.type === "cookie" ? "include" : "omit",
    });
  };
}

export function createUsersApi(baseUrl: string, auth: AuthStrategy) {
  const authBase = `${baseUrl}/api/auth`;
  const authFetch = createAuthFetch(auth);

  async function listUsers(
    options?: ListUsersOptions
  ): Promise<Result<ListUsersResponse, AdminApiError>> {
    const params = new URLSearchParams();
    if (options?.limit) {
      params.set("limit", String(options.limit));
    }
    if (options?.offset) {
      params.set("offset", String(options.offset));
    }
    if (options?.search) {
      params.set("search", options.search);
    }

    const url = `${authBase}/admin/list-users${params.toString() ? `?${params}` : ""}`;
    const response = await authFetch(url);

    if (!response.ok) {
      const error = (await response.json()) as AdminApiError;
      return Result.err(error);
    }

    const data = (await response.json()) as ListUsersResponse;
    return Result.ok(data);
  }

  async function banUser(
    userId: string,
    banReason?: string
  ): Promise<Result<{ success: boolean }, AdminApiError>> {
    const response = await authFetch(`${authBase}/admin/ban-user`, {
      method: "POST",
      body: JSON.stringify({ userId, banReason }),
    });

    if (!response.ok) {
      const error = (await response.json()) as AdminApiError;
      return Result.err(error);
    }

    return Result.ok({ success: true });
  }

  async function unbanUser(
    userId: string
  ): Promise<Result<{ success: boolean }, AdminApiError>> {
    const response = await authFetch(`${authBase}/admin/unban-user`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const error = (await response.json()) as AdminApiError;
      return Result.err(error);
    }

    return Result.ok({ success: true });
  }

  async function setRole(
    userId: string,
    role: Role
  ): Promise<Result<{ success: boolean }, AdminApiError>> {
    const response = await authFetch(`${authBase}/admin/set-role`, {
      method: "POST",
      body: JSON.stringify({ userId, role }),
    });

    if (!response.ok) {
      const error = (await response.json()) as AdminApiError;
      return Result.err(error);
    }

    return Result.ok({ success: true });
  }

  async function removeUser(
    userId: string
  ): Promise<Result<{ success: boolean }, AdminApiError>> {
    const response = await authFetch(`${authBase}/admin/remove-user`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const error = (await response.json()) as AdminApiError;
      return Result.err(error);
    }

    return Result.ok({ success: true });
  }

  return {
    listUsers,
    banUser,
    unbanUser,
    setRole,
    removeUser,
  };
}

export type UsersApi = ReturnType<typeof createUsersApi>;
